import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readLayout } from "@/office/word/layout";

export const lektionKopfFusszeile: Lesson = {
  id: "kopf-und-fusszeile",
  title: "Kopf- und Fußzeile einrichten",
  summary: "Der Bereich außerhalb des Textes – und wofür er gedacht ist.",
  durationMinutes: 8,
  learningGoals: [
    "Kopf- und Fußzeile öffnen und bearbeiten",
    "Passende Inhalte für beide Bereiche auswählen",
    "Den Bearbeitungsmodus sicher verlassen",
  ],
  steps: [
    {
      id: "kopfzeile",
      title: "Die Kopfzeile",
      explanation: [
        "Kopf- und Fußzeile liegen im oberen und unteren Seitenrand und wiederholen sich " +
          "automatisch auf jeder Seite. Was du dort einträgst, musst du also genau einmal " +
          "schreiben.",
        "In die Kopfzeile gehört, woran man das Dokument beim Blättern wiedererkennt: " +
          "Titel des Berichts, Firmenname, manchmal das aktuelle Kapitel. In die Fußzeile " +
          "kommen Seitenzahl, Datum und rechtliche Hinweise.",
        "Du öffnest den Bereich mit einem Doppelklick in den oberen oder unteren " +
          "Seitenrand. Word wechselt dann in einen eigenen Bearbeitungsmodus – der " +
          "Haupttext wird ausgegraut. Mit einem Doppelklick in den Text oder der " +
          "Esc-Taste kommst du wieder heraus.",
      ],
      keyPoints: [
        "Doppelklick in den Seitenrand öffnet die Kopf- bzw. Fußzeile.",
        "Esc oder Doppelklick in den Text beendet die Bearbeitung.",
        "Der Inhalt wiederholt sich automatisch auf allen Seiten des Abschnitts.",
      ],
      task: {
        instruction: "Trage in die Kopfzeile deines Dokuments einen Text ein, zum Beispiel den Dokumenttitel.",
        hint: "Doppelklick in den oberen Seitenrand, Text tippen, danach Esc drücken.",
        check: async () => {
          const layout = await readLayout();
          if (!layout.hasHeaderText) {
            return fail(
              "In der Kopfzeile steht noch kein Text.",
              "Der Doppelklick muss in den weißen Bereich oberhalb des Textes gehen, nicht in die erste Textzeile.",
            );
          }
          const text = layout.sections.find((abschnitt) => abschnitt.headerText.length > 0);
          return pass(`Kopfzeile erkannt: „${text?.headerText.slice(0, 50)}“.`);
        },
      },
    },
    {
      id: "fusszeile",
      title: "Die Fußzeile",
      explanation: [
        "Die Fußzeile funktioniert genauso, hat aber eine andere Aufgabe. Hier steht, was " +
          "zur Einordnung des Dokuments gebraucht wird: Seitenzahl, Stand oder Datum, " +
          "Vertraulichkeitsvermerk, Aktenzeichen.",
        "Ein Hinweis zu Abschnitten: Hast du in der letzten Lektion einen Abschnittsumbruch " +
          "eingefügt, kann jeder Abschnitt eine eigene Kopf- und Fußzeile bekommen. " +
          "Standardmäßig übernimmt Word aber die des vorherigen Abschnitts – erkennbar am " +
          "Hinweis „Wie vorherige“ am Rand. Erst wenn du diese Verknüpfung über die " +
          "Schaltfläche „Mit vorheriger verknüpfen“ löst, lassen sich die Bereiche " +
          "unterschiedlich befüllen.",
      ],
      keyPoints: [
        "Fußzeile: Seitenzahl, Datum, Vertraulichkeitsvermerk.",
        "„Wie vorherige“ bedeutet: Der Abschnitt übernimmt die Fußzeile des vorherigen.",
        "Die Verknüpfung löst du im Menüband unter „Kopf- und Fußzeile“.",
      ],
      task: {
        instruction: "Trage jetzt auch in die Fußzeile einen Text ein.",
        hint: "Doppelklick in den unteren Seitenrand.",
        check: async () => {
          const layout = await readLayout();
          return fromCriteria(
            [
              criterion("Die Kopfzeile enthält Text", layout.hasHeaderText),
              criterion("Die Fußzeile enthält Text", layout.hasFooterText),
            ],
            {
              success: "Beide Bereiche sind gefüllt – sie erscheinen jetzt auf jeder Seite.",
              failure: "Ein Bereich ist noch leer.",
              hint: "Zwischen Kopf- und Fußzeile wechselst du im Bearbeitungsmodus mit der Schaltfläche „Zu Fußzeile wechseln“.",
            },
          );
        },
      },
    },
  ],
};
